import { TRACKED_KEYWORD } from '@modules/common/constants';
import {
  getFallbackDisplayNameForEqlField,
  getFallbackDisplayNameForEqlFieldValueIfRequired,
} from '@modules/queryBuilder/helpers';
import { EqlFieldByID } from '@modules/queryBuilder/hooks/useFetchEQLReferences';
import { toEntityValues } from 'v2/core/repository/eql/mappers';
import { operatorFromFilter } from 'v2/core/services/queryBuilder/operatorFromFilter';
import { WhereClause } from 'v2/core/services/queryBuilder/whereClause';
import { getFormattedDisplayNameForEqlField } from '../components/EqlFieldsAutoComplete/helpers';
import { QueryStringOptionFromFilter } from '../types';

export const getQueryStringOptionFromFilter = (
  whereClause: WhereClause,
  eqlFieldByID: EqlFieldByID,
  isUnifiedFieldEnabled: boolean,
  shouldRemoveFileUploadPrefix: boolean,
): QueryStringOptionFromFilter => {
  if (!whereClause.filter) {
    return;
  }

  const operator = operatorFromFilter(whereClause.filter, whereClause.exclude);

  if (whereClause.filter.entityFilter) {
    return {
      fieldName: 'Entity',
      fieldDisplayName:
        eqlFieldByID['entity']?.field?.displayName ?? TRACKED_KEYWORD,
      values: toEntityValues(whereClause.filter.entityFilter.entities).map(
        (trackedKeywordID) => {
          const isEntitySentiment = trackedKeywordID.includes(':');

          if (isEntitySentiment) {
            const [actualTrackedKeywordID, sentiment] =
              trackedKeywordID.split(':');

            return `${
              eqlFieldByID['entity']?.eqlFieldValueByID?.[
                actualTrackedKeywordID
              ]?.displayName ?? trackedKeywordID
            }${sentiment ? ` - ${sentiment}` : ''}`;
          }

          return (
            eqlFieldByID['entity']?.eqlFieldValueByID?.[trackedKeywordID]
              ?.displayName ?? trackedKeywordID
          );
        },
      ),
      operator,
    };
  }

  if (whereClause.filter.reasonFilter) {
    return {
      fieldName: 'Reason',
      fieldDisplayName: eqlFieldByID['reason']?.field?.displayName ?? 'Reason',
      values: whereClause.filter.reasonFilter.reasonIDs.map(
        (reasonID) =>
          eqlFieldByID['reason']?.eqlFieldValueByID?.[reasonID]?.displayName ??
          reasonID,
      ),
      operator,
    };
  }

  if (whereClause.filter.l1Filter) {
    return {
      fieldName: 'L1',
      fieldDisplayName: eqlFieldByID['l1']?.field?.displayName ?? 'L1',
      values: whereClause.filter.l1Filter.l1IDs.map((l1ID) => {
        return (
          eqlFieldByID['l1']?.eqlFieldValueByID?.[l1ID]?.displayName ?? l1ID
        );
      }),
      operator,
    };
  }

  if (whereClause.filter.languageFilter) {
    return {
      fieldName: 'Language',
      fieldDisplayName:
        eqlFieldByID['language']?.field?.displayName ?? 'Language',
      values: whereClause.filter.languageFilter.iso6393s.map(
        (language) =>
          eqlFieldByID['language']?.eqlFieldValueByID?.[language]
            ?.displayName ?? language,
      ),
      operator,
    };
  }

  if (whereClause.filter.sourceFilter) {
    return {
      fieldName: 'Source',
      fieldDisplayName: eqlFieldByID['source']?.field?.displayName ?? 'Source',
      values: whereClause.filter.sourceFilter.sources.map((source) => {
        const valueKey = source.namespace
          ? `${source.name}-${source.namespace}`
          : source.name;

        return (
          eqlFieldByID['source']?.eqlFieldValueByID?.[valueKey]?.displayName ??
          valueKey
        );
      }),
      operator,
    };
  }

  if (whereClause.filter.reviewSentimentFilter) {
    return {
      fieldName: 'ReviewSentiment',
      fieldDisplayName:
        eqlFieldByID['sentiment']?.field?.displayName ?? 'Sentiment',
      values: whereClause.filter.reviewSentimentFilter.sentimentIDs.map(
        (sentimentID) =>
          eqlFieldByID['sentiment']?.eqlFieldValueByID?.[sentimentID]
            ?.displayName ?? sentimentID,
      ),
      operator,
    };
  }

  if (whereClause.filter.hybridSearchFilter) {
    return {
      fieldName: 'Search',
      fieldDisplayName: 'Search',
      values: whereClause.filter.hybridSearchFilter.queries ?? [],
      operator,
    };
  }

  if (whereClause.filter.hasAspectFilter) {
    return {
      fieldName: 'IsEntityPresent',
      fieldDisplayName:
        eqlFieldByID['entity']?.field?.displayName ?? TRACKED_KEYWORD,
      values: [],
      operator,
    };
  }

  if (whereClause.filter.hasReasonFilter) {
    return {
      fieldName: 'IsReasonPresent',
      fieldDisplayName: eqlFieldByID['reason']?.field?.displayName ?? 'Reason',
      values: [],
      operator,
    };
  }

  if (whereClause.filter.hasUserContentFilter) {
    return {
      fieldName: 'Feedback',
      fieldDisplayName: 'Feedback',
      values: [],
      operator,
    };
  }

  if (whereClause.filter.hasMetadataFilter) {
    const metadataKey = whereClause.filter?.hasMetadataFilter?.keys?.[0] ?? '';
    const eqlField = eqlFieldByID[metadataKey]?.field;

    const fieldDisplayName = eqlField?.displayName
      ? getFormattedDisplayNameForEqlField({
          shouldRemoveFileUploadPrefix: shouldRemoveFileUploadPrefix,
          eqlField: eqlField ?? null,
          isUnifiedFieldEnabled: isUnifiedFieldEnabled,
        })
      : getFallbackDisplayNameForEqlField({
          field: metadataKey,
          shouldShowSource: true,
        });

    return {
      fieldName: 'Metadata',
      fieldDisplayName,
      values: [],
      operator,
    };
  }

  if (whereClause.filter.metadataFilter) {
    const metadataKey = whereClause.filter?.metadataFilter?.key ?? '';
    const eqlField = eqlFieldByID[metadataKey]?.field;

    const fieldDisplayName = eqlField?.displayName
      ? getFormattedDisplayNameForEqlField({
          shouldRemoveFileUploadPrefix: shouldRemoveFileUploadPrefix,
          eqlField: eqlField ?? null,
          isUnifiedFieldEnabled: isUnifiedFieldEnabled,
        })
      : getFallbackDisplayNameForEqlField({
          field: metadataKey,
          shouldShowSource: true,
        });

    return {
      fieldName: 'Metadata',
      fieldDisplayName,
      values: whereClause.filter.metadataFilter.values.map(
        (value) =>
          eqlFieldByID[metadataKey]?.eqlFieldValueByID?.[String(value)]
            ?.displayName ??
          getFallbackDisplayNameForEqlFieldValueIfRequired({
            field: metadataKey,
            value: String(value),
          }),
      ),
      metadataKey,
      operator,
      eqlField: eqlFieldByID[metadataKey]?.field,
    };
  }

  if (whereClause.filter.categoryFilter) {
    return {
      fieldName: 'Category',
      fieldDisplayName:
        eqlFieldByID['category']?.field?.displayName ?? 'Category',
      values: whereClause.filter.categoryFilter.categories.map(
        (category) =>
          eqlFieldByID['category']?.eqlFieldValueByID?.[category]
            ?.displayName ?? category,
      ),
      operator,
    };
  }

  if (whereClause.filter.hasSummaryFilter) {
    return {
      fieldName: 'Summary',
      fieldDisplayName:
        eqlFieldByID['summary']?.field?.displayName ?? 'Summary',
      values: [],
      operator,
    };
  }
};
